import { Link } from "react-router-dom";
import { formatDate } from "@/lib/content";
import type { Article } from "@/mocks/articles";

interface ArticleCardProps {
  article: Article;
  variant?: "grid" | "list";
}

const categoryStyle: Record<string, { label: string; className: string }> = {
  laws: { label: "法律法规", className: "bg-primary-600 text-background-50" },
  ethics: { label: "伦理道德", className: "bg-secondary-600 text-background-50" },
  cases: { label: "案例分析", className: "bg-accent-600 text-background-50" },
};

export default function ArticleCard({ article, variant = "grid" }: ArticleCardProps) {
  const meta = categoryStyle[article.category] ?? {
    label: "内容",
    className: "bg-foreground-700 text-background-50",
  };

  const isList = variant === "list";

  return (
    <article
      className={[
        "group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all duration-300 hover:-translate-y-1 hover:border-primary-200 hover:shadow-lift",
        isList ? "flex flex-col sm:flex-row" : "flex h-full flex-col",
      ].join(" ")}
    >
      <Link
        to={`/detail/${article.id}`}
        className={[
          "relative block shrink-0 overflow-hidden",
          isList ? "h-48 w-full sm:h-auto sm:w-60" : "h-48 w-full",
        ].join(" ")}
        aria-label={article.title}
      >
        <img
          src={article.cover}
          alt={`${article.title} 配图`}
          title={`${article.title} - 乱伦专题配图`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span
          className={[
            "absolute left-3 top-3 rounded-md px-2.5 py-1 text-xs font-medium",
            meta.className,
          ].join(" ")}
        >
          {meta.label}
        </span>
      </Link>

      <div className={["flex flex-1 flex-col p-4 md:p-5", isList ? "" : ""].join(" ")}>
        <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line" />
            {formatDate(article.date)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line" />
            约 {article.readTime} 分钟
          </span>
          {article.region && (
            <span className="flex items-center gap-1">
              <i className="ri-map-pin-2-line" />
              {article.region}
            </span>
          )}
        </div>

        <h3 className="mb-2 text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
          <Link to={`/detail/${article.id}`}>{article.title}</Link>
        </h3>

        <p className="mb-4 flex-1 text-sm leading-relaxed text-foreground-600 line-clamp-3">
          {article.summary}
        </p>

        <div className="flex flex-wrap items-center gap-2">
          {article.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-background-100 px-2.5 py-0.5 text-xs text-foreground-600"
            >
              {tag}
            </span>
          ))}
          <Link
            to={`/detail/${article.id}`}
            className="ml-auto inline-flex items-center gap-1 whitespace-nowrap text-xs font-medium text-primary-700 hover:text-primary-800"
          >
            阅读全文
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </article>
  );
}